import { userApi } from '@/apis/core';
import { useMutation, UseMutationResult, UseMutationOptions } from '@tanstack/react-query';
import {
  UpdatePasswordRequest,
  UpdateMemberRequest,
  UpdateEmailRequest,
  GetMemberListForAdmin200ResponseBody,
  DeleteUserRequest,
  GetMember200ResponseBody,
  GetRole200ResponseBody,
  GetMember200Response,
  RegisterLineMessagingIdRequest
} from 'openapi/omise-craft-core-spec';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import HttpStatusCode from '../common/HttpStatusCode';
import { useAtom } from 'jotai';
import { ErrorMessageAtom } from '@/utility/CommonAtoms';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMutationWrapper } from '@/apis/common/useWrapper';

/**
 * パスワード変更の型定義
 */
export type updatePasswordType = {
  uid: string | undefined;
  param: UpdatePasswordRequest;
};

export type updateMemberType = {
  id: string;
  param: UpdateMemberRequest;
};

export type roleType = {
  uid: string;
  role: string;
  requestConfig: AxiosRequestConfig;
};

/**
 * パスワード変更
 *
 * @returns パスワード変更できたか結果を返す
 */
export const UseUpdatePassword = () => {
  return UseMutation({
    mutationKey: ['createPasswordReset'],
    mutationFn: async (param: updatePasswordType) => {
      console.warn(param);
      return await userApi.updatePassword(param.uid ?? undefined, param.param);
    }
  });
};

/**
 * メールアドレス変更
 *
 * @returns メール送信完了したか結果を返す
 */
export const useUpdateEmail = () => {
  return UseMutation({
    mutationKey: ['updateEmail'],
    mutationFn: async (param: UpdateEmailRequest & { uid?: string }) => {
      console.warn(param);
      const { uid, ...req } = param;
      return await userApi.updateEmail(uid, req);
    }
  });
};

/**
 * APIを実行
 *
 * @param options
 * @returns
 */
const UseMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'onError'> & {
    getErrorMessage?: (httpStatus?: HttpStatusCode) => string;
  }
): UseMutationResult<TData, TError, TVariables, TContext> => {
  return useMutation({
    ...options
  });
};

/**
 * ロール情報を取得
 *
 * @param {string?} uid ユーザid
 *
 * @return {GetRole200ResponseBody} ロール情報
 */
export const getRole = async (uid?: string) => {
  const res = uid !== undefined ? await userApi.getRole(uid) : await userApi.getRole();
  return res.data.body;
};

/**
 * ロール情報を取得するtanstack-query
 *
 * @param {string} uid サービス側ユーザid
 *
 * @return {UseQueryResult<TQueryFnData, Error>} ロール情報を含むUseQueryResult: { data: unknown, isSuccess: boolean, isError: boolean, isLoading: boolean, error: unknown }
 */
export const useGetRole = (uid?: string) => {
  return useQuery<GetRole200ResponseBody | undefined, Error>(['getRole'], () => getRole(uid));
};

// ユーザ情報取得（管理者ユーザ用）
// usr=XXXでユーザID指定が可能
export const getMember = async (uid?: string, check_feature_noti_version?: boolean) => {
  const res =
    uid !== undefined
      ? await userApi.getMember(uid, undefined)
      : await userApi.getMember(undefined, check_feature_noti_version);
  return res.data.body;
};

/**
 * メンバー情報を取得
 *
 * @param {string|undefined} uid ユーザid / 指定された場合は指定されたユーザidを検索する
 * @param {check_feature_noti_version} uid trueの場合versionを上げる
 *
 * @return {UseQueryResult<GetMember200ResponseBody>} メンバー情報
 */
export const useGetMember = (
  uid?: string,
  check_feature_noti_version?: boolean,
  options?: {
    enabled?: boolean;
  }
): UseQueryResult<GetMember200ResponseBody> => {
  return useQuery<GetMember200ResponseBody, Error>(
    ['option_use_member'],
    () => getMember(uid, check_feature_noti_version),
    { enabled: options?.enabled ?? true }
  );
};

// ユーザ情報更新
const updateMember = async (uid?: string, updateMemberRequest?: UpdateMemberRequest) => {
  await userApi.updateMember(uid, updateMemberRequest);
};

export const useUpdateMember = (uid?: string) => {
  return useUpdateMemberMutation({
    mutationKey: ['createUpdateMember'],
    mutationFn: (param: updateMemberType) => {
      console.warn(param);
      return updateMember(uid, param.param);
    }
  });
};

const useUpdateMemberMutation = <TData = unknown, TError = unknown, TVariables = void, TContext = unknown>(
  options?: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'onError'> & {
    getErrorMessage?: (httpStatus?: HttpStatusCode) => string;
  }
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const [, setMessage] = useAtom(ErrorMessageAtom);
  return useMutation({
    ...options,
    onError: (error) => {
      if (error instanceof AxiosError) {
        const errorMessage = options?.getErrorMessage?.(error.response?.status);
        if (errorMessage) {
          setMessage(errorMessage);
        } else {
          setMessage('一時的なエラーが発生しました。お手数ですが、しばらく経ってからお試しください。');
          if (error.response?.data?.errors) {
            console.warn(error.response.data.errors);
          }
        }
      }
    }
  });
};

// 退会
const deleteUser = async (uid?: string, deleteUserRequest?: DeleteUserRequest) => {
  await userApi.deleteUser(uid, deleteUserRequest);
};

export const UseDeleteUser = () => {
  return useMutationWrapper({
    mutationKey: ['deleteUser'],
    mutationFn: (param: DeleteUserRequest) => {
      console.warn(param);
      return deleteUser(undefined, param);
    }
  });
};

/**
 * メールアドレス変更確認
 *
 * @param hash ハッシュ値
 * @returns
 */
export const useGetConfirmEmail = (hash: string) => {
  return useQuery<object, Error>(['option_categories'], () => getConfirmEmail(hash), {
    retry: false,
    refetchOnWindowFocus: false
  });
};

/**
 * メールアドレス変更確認（API実行）
 *
 * @param hash ハッシュ値
 * @returns
 */
const getConfirmEmail = async (hash: string) => {
  return await userApi.confirmEmail(hash);
};

// ユーザ情報一覧取得（管理者ユーザ用）
export type GetMemberListForAdminType = {
  memberId?: number;
  repConsultantId?: number;
  emailAddress?: string;
  name?: string;
  qualifiedRank?: number;
  page?: number;
};

export const getMemberListForAdmin = async (param: GetMemberListForAdminType) => {
  const res = await userApi.getMemberListForAdmin(
    param.memberId,
    param.repConsultantId,
    param.emailAddress,
    param.name,
    param.qualifiedRank,
    param.page
  );
  return res.data.body;
};

export const useGetMemberListForAdmin = (param: GetMemberListForAdminType) => {
  return useQuery<GetMemberListForAdmin200ResponseBody, Error>(['member_list'], () => getMemberListForAdmin(param));
};

export const useUpdateMailLetter = (isAgreed: boolean) => {
  return useMutation<object, Error>(() => updateMailLetter(isAgreed));
};

const updateMailLetter = async (isAgreed: boolean) => {
  try {
    const response = await userApi.updateMailLetter(isAgreed);
    console.log('API Response:', response);
    return response;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const useUpdateQualifiedRankForce = () => {
  return UseMutation({
    mutationKey: ['updateQualifiedRankForce'],
    mutationFn: async (param: { uid: string; qualified?: number }) => {
      return updateQualifiedRankForce(param.uid, param.qualified);
    }
  });
};

const updateQualifiedRankForce = async (uid: string, qualified?: number) => {
  try {
    const response = await userApi.updateQualifiedRankForce(uid, qualified);
    console.log('API Response:', response);
    return response;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

export const getMemberListCsvForAdmin = async () => {
  const res: AxiosResponse<File> = await userApi.getMemberListCsvForAdmin();
  const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });

  // 任意のファイル名（必要なら Content-Disposition ヘッダーから取得しても良い）
  const d = new Date();
  const filename = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(
    2,
    '0'
  )}_member_list.csv`;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useGetMemberCsvListForAdmin = (param: GetMemberListForAdminType) => {
  return useQuery<GetMemberListForAdmin200ResponseBody, Error>(['member_list'], () => getMemberListForAdmin(param));
};

export const isRegisteredEmail = async (email: string) => {
  const res = await userApi.isRegisteredEmail(email);
  return res.data;
};

export const useIsRegisteredEmail = (email: string, enabled: boolean) => {
  return useQuery<boolean, Error>(['isRegisteredEmail'], () => isRegisteredEmail(email), {
    enabled: enabled
  });
};

export const getMemberByPhoneNumber = async (phoneNumber: string) => {
  const res = await userApi.getMemberByPhoneNumber(phoneNumber);
  return res.data;
};

export const useGetMemberByPhoneNumber = (phoneNumber: string, enabled: boolean) => {
  return useQuery<GetMember200Response, Error>(['GetMemberByPhoneNumber'], () => getMemberByPhoneNumber(phoneNumber), {
    enabled: enabled
  });
};

const registerLineMessagingId = async (memberId: number, lineId: string, displayName: string) => {
  const request: RegisterLineMessagingIdRequest = {
    memberId: memberId,
    lineMessagingId: lineId,
    lineMessagingDisplayName: displayName
  };
  await userApi.registerLineMessagingId(request);
};

export const useRegisterLineMessagingId = () => {
  return useMutationWrapper({
    mutationKey: ['registerLineMessagingId'],
    mutationFn: (param: { memberId: number; lineId: string; displayName: string }) => {
      return registerLineMessagingId(param.memberId, param.lineId, param.displayName);
    }
  });
};

export const adminGetMember: (memberId: number) => Promise<GetMember200Response> = async (memberId: number) => {
  const res = await userApi.adminGetMember(memberId);
  return res.data;
};

export const useAdminGetMember = (memberId: number, enabled?: boolean) => {
  return useQuery(['adminGetMember', memberId], () => adminGetMember(memberId), {
    enabled: enabled
  });
};
